<template>
  <form-modal
    :ref="modalName"
    :name="modalName"
    class="NewSearchSource"
    title="New Feed Source"
    submit-name="Create"
    full-height
    :submit-disabled="!sourceValidated"
    @submit="submit"
    @[Events.AFTER_CLOSE]="modalClosed"
  >
    <template v-if="currentLandscape">
      <lm-actor-select
        ref="actorSelect"
        :selected-actor="selectedActor"
        check-rights
      >
        Add feed to:
      </lm-actor-select>
      <lm-label>Feed source:</lm-label>
      <div
        class="h-32 w-40 mb-8 py-4 px-2 cursor-pointer border bg-gray-200 border-gray-700"
      >
        <header class="flex items-center mb-2">
          <lm-icon size="21" icon="lmsearch" class="mr-2 text-gray-700" />
          <h1 class="text-xs text-gray-900">
            {{ FeedSourcesLabels.LMSEARCH }}
          </h1>
        </header>
        <p class="mb-0 text-xxs text-gray-500">
          {{ FeedSourcesDescription.LMSEARCH }}
        </p>
      </div>
      <div class="flex items-center">
        <lm-input
          ref="inputUrl"
          class="flex-grow"
          label="Search"
          focus
          :maxlength="0"
          @change="checkURL"
        />
        <lm-validator-icon v-if="urlState" :state="urlState" />
      </div>
      <lm-input
        ref="inputTitle"
        label="Title"
        focus
        @submit="submitFromInput"
      />
      <lm-textarea
        ref="inputDescription"
        label="Description (optional)"
        :rows="2"
        :allow-breakline="false"
        @submit="submitFromInput"
      />
      <lm-tags-selector
        ref="tagSelector"
        class="mb-16"
        :options="currentLandscape.tags"
      />
    </template>
  </form-modal>
</template>

<script>
import LmActorSelect from '@/components/form/LmActorSelect'
import LmIcon from '@/components/ui/LmIcon'
import LmLabel from '@/components/ui/LmLabel'
import {mapGetters} from 'vuex'
import {
  Events,
  Modal,
  FeedSources,
  FeedSourcesLabels,
  FeedSourcesDescription
} from '@/lib/definitions'
import EventHub from '@/lib/eventHub'
import FormModal from '@/components/modals/FormModal'
import LmInput from '@/components/form/LmInput.vue'
import FormHelper from '@/components/form/formHelper'
import LmTextarea from '@/components/form/LmTextarea.vue'
import LmTagsSelector from '@/components/form/LmTagsSelector'
import LmValidatorIcon from '@/components/ui/LmValidatorIcon'

export default {
  name: 'ModalNewSearchSource',
  components: {
    LmLabel,
    LmIcon,
    LmActorSelect,
    LmValidatorIcon,
    LmTextarea,
    LmTagsSelector,
    FormModal,
    LmInput
  },
  data() {
    return {
      Events,
      FeedSources,
      FeedSourcesLabels,
      FeedSourcesDescription,
      modalName: Modal.NEW_SEARCH_SOURCE,
      modalRef: null,
      inputs: null,
      tags: [],
      sourceValidated: false,
      urlState: null
    }
  },
  computed: {
    ...mapGetters(['selectedActor', 'currentLandscape'])
  },
  mounted() {
    this.modalRef = this.$refs[this.modalName]
    this.inputs = [
      this.$refs.actorSelect,
      this.$refs.inputUrl,
      this.$refs.inputTitle,
      this.$refs.inputDescription,
      this.$refs.tagSelector
    ]
    this.init()
  },
  methods: {
    init() {
      if (this.$route.query.text && this.$refs.inputUrl) {
        const text = this.$route.query.text
        this.$refs.inputUrl.setData(text)
        this.checkURLAgain()
      }
    },
    submitFromInput() {
      this.modalRef.submit()
    },
    checkURLAgain() {
      this.sourceValidated = false
      this.$refs.inputUrl.clearErrors()
      this.$refs.inputTitle.reset()
      this.$refs.inputDescription.reset()
      this.checkURL(this.$refs.inputTitle.data)
    },
    checkURL(e) {
      if (this.sourceValidated && this.$refs.inputTitle.data !== e) {
        this.urlState = 'invalid'
        this.sourceValidated = false // Validated source has changed! Check again
      }
      if (!this.sourceValidated && this.$refs.inputUrl.data !== '') {
        this.urlState = 'validating'
        const payload = {
          type: FeedSources.LMSEARCH,
          source: this.$refs.inputUrl.data
        }
        this.$store
          .dispatch('checkSource', payload)
          .then(source => {
            this.$refs.inputTitle.setData(source.title)
            const desc = source.description ? source.description : ''
            this.$refs.inputDescription.setData(desc)
            this.sourceValidated = true
            this.urlState = 'validated'
          })
          .catch(e => this.showUrlError(e))
      }
    },
    showUrlError(error) {
      this.urlState = 'invalid'
      this.sourceValidated = false
      this.$refs.inputUrl.error = true
      this.$refs.inputUrl.errorMessage = 'Url not found'
      if (error && error.devCode === 404) {
        this.$refs.inputUrl.errorMessage = error.devMessage
      }
    },
    submit() {
      if (!this.sourceValidated) return
      if (FormHelper.validate(this.inputs)) {
        let payload = {
          actorId: this.$refs.actorSelect.data,
          type: FeedSources.LMSEARCH,
          source: this.$refs.inputUrl.data,
          title: this.$refs.inputTitle.data,
          description: this.$refs.inputDescription.data,
          tags: this.$refs.tagSelector.data,
          includedKeywords: [],
          excludedKeywords: []
        }
        this.$store
          .dispatch('createSource', payload)
          .then(() => this.modalRef.submitSuccess())
          .catch(error => {
            this.modalRef.submitFails(error.devMessage)
          })
      } else {
        this.modalRef.submitFails()
      }
    },
    modalClosed({fromSubmit}) {
      if (fromSubmit) {
        EventHub.emit(
          Events.SHOW_SNACKBAR,
          'New Source created',
          'Go to Sources',
          this.goToSources
        )
      }
    },
    goToSources() {
      this.$router.push({name: 'feed-sources'})
    }
  }
}
</script>
